import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import type { Tenant } from '@/types/auth';

/** Fields of the PATCH /tenant answer that the shell (sidebar, topbar, receipt preview) reads from the auth store. */
const SHELL_FIELDS = ['name', 'slug', 'brandLogoUrl', 'brandColor', 'brandTagline', 'testMode'] as const;

/** POS-I286: pick only the shell fields that the server answer actually carries. */
export function pickTenantShellFields(res: unknown): Partial<Tenant> {
  if (!res || typeof res !== 'object') return {};
  const src = res as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of SHELL_FIELDS) if (k in src && src[k] !== undefined) out[k] = src[k];
  return out as Partial<Tenant>;
}

/** Merge the answer over the cached tenant (never replace: settings and trial fields must survive). */
export function mergeTenantCache<T extends object>(prev: T | undefined, res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return prev;
  return prev ? { ...prev, ...(res as object) } : (res as T);
}

/**
 * POS-I286: after any tenant save, show the new values everywhere at once: the shared auth store
 * (sidebar header, topbar, cards) and the tenant query, then refetch to stay in step with the server.
 */
export function applyTenantUpdate(queryClient: QueryClient, res: unknown): void {
  const patch = pickTenantShellFields(res);
  const { tenant, setTenant } = useAuthStore.getState();
  if (tenant && Object.keys(patch).length > 0) setTenant({ ...tenant, ...patch });
  queryClient.setQueryData(['tenant-current'], (prev: object | undefined) => mergeTenantCache(prev, res));
  void queryClient.invalidateQueries({ queryKey: ['tenant-current'] });
}
